import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil, interval, tap, switchMap, takeWhile, firstValueFrom } from 'rxjs';
import { WhatsAppInstanciaService } from '../../services/whatsapp-instancia.service';
import { StatusInstanciaResponse, QrCodeResponse } from '../../models/whatsapp-instancia.model';
import { ConfiguracoesWhatsAppService } from '../../services/configuracoes-whatsapp.service';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { jwtDecode } from 'jwt-decode';
import * as QRCode from 'qrcode';

@Component({
  selector: 'app-whatsapp-config',  
  templateUrl: './whatsapp-config.component.html',
  styleUrls: ['./whatsapp-config.component.scss']
})
export class WhatsappConfigComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Status do WhatsApp
  sessionId: string | null = null;
  status: string = 'disconnected'; // "initializing" | "qr_ready" | "connected" | "disconnected"
  phoneNumber: string | null = null;
  connectedAt: string | null = null;
  qrCode: string | null = null;
  qrCodeExpiresAt: string | null = null;
  isLoading = false;
  isConnecting = false;
  isDisconnecting = false;
  isWaitingForQRCode = false; // Flag para quando estiver aguardando 5s para consultar QR code
  
  // Auto-refresh do status
  private readonly statusRefreshInterval = 5000000; // 20 segundos (dobrado de 10s)

  // Configurações
  quantidadeChamadosSimultaneos: number = 1;
  isSavingConfig = false;
  isLoadingConfig = false;

  constructor(
    private readonly whatsappInstanciaService: WhatsAppInstanciaService,
    private readonly configuracoesWhatsAppService: ConfiguracoesWhatsAppService,
    private readonly toastService: ToastService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    // Obter sessionId do tenantId
    this.sessionId = this.getTenantId();
    
    // Verificar se já existe instância ao carregar
    // if (this.sessionId) {
    //   this.verificarInstanciaExistente();
    // }
    
    // Iniciar verificação periódica se não estiver conectado
    this.startStatusRefresh();
    
    // Carregar configurações
    this.carregarConfiguracoes();

    this.loadWhatsAppStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verificar se já existe instância ao carregar componente
   */
  async verificarInstanciaExistente(): Promise<void> {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) return;
    }

    try {
      const statusResponse = await firstValueFrom(
        this.whatsappInstanciaService.obterStatus(this.sessionId).pipe(takeUntil(this.destroy$))
      );
      
      if (statusResponse) {
        this.atualizarStatus(statusResponse);
        
        if (statusResponse.status === 'qr_ready') {
          await this.loadQRCode();
        }
      }
    } catch (error: any) {
      // Instância não existe ainda ou erro ao buscar
      if (error.status !== 404) {
        console.error('Erro ao verificar instância existente:', error);
      }
    }
  }

  /**
   * Carregar status do WhatsApp
   * GET /api/v1/WhatsAppInstancias/instances/{sessionId}/status
   */
  async loadWhatsAppStatus(): Promise<void> {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) return;
    }

    this.isLoading = true;
    try {
      const statusResponse = await firstValueFrom(
        this.whatsappInstanciaService.obterStatus(this.sessionId).pipe(takeUntil(this.destroy$))
      );
      
      this.atualizarStatus(statusResponse);
      
      // Se status for "qr_ready", buscar QR code imediatamente
      if (statusResponse.status === 'qr_ready' && !this.isWaitingForQRCode) {
        await this.loadQRCode();
      } else if (statusResponse.status === 'connected') {
        this.qrCode = null;
        this.qrCodeExpiresAt = null;
        this.isWaitingForQRCode = false;
      }
    } catch (error: any) {
      console.error('Erro ao carregar status:', error);
      
      if (error.status === 401) {
        this.toastService.show('Erro de autenticação na API WhatsApp. Verifique as credenciais.', {
          classname: 'bg-warning text-dark'
        });
      } else if (error.status === 404) {
        // Instância não existe
        this.status = 'disconnected';
        this.phoneNumber = null;
        this.connectedAt = null;
        this.isWaitingForQRCode = false;
      } else {
        this.toastService.show('Erro ao conectar com a API WhatsApp. Verifique se a API está rodando.', {
          classname: 'bg-danger text-light'
        });
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Atualizar estado do componente com resposta do status
   */
  private atualizarStatus(statusResponse: StatusInstanciaResponse): void {
    const previousStatus = this.status;
    this.status = statusResponse.status || 'disconnected';
    this.phoneNumber = statusResponse.phoneNumber || null;
    this.connectedAt = statusResponse.connectedAt || null;
    this.sessionId = statusResponse.sessionId || this.sessionId;
    
    // Se recebeu status "initializing", aguardar 5 segundos e consultar QR code
    if (this.status === 'initializing' && previousStatus !== 'initializing') {
      this.aguardarEconsultarQRCode();
    }
  }

  /**
   * Aguardar 5 segundos e então consultar o QR code
   */
  private aguardarEconsultarQRCode(): void {
    this.isWaitingForQRCode = true;
    
    setTimeout(async () => {
      this.isWaitingForQRCode = false;
      if (this.status === 'initializing' || this.status === 'qr_ready') {
        await this.loadQRCode();
      }
    }, 5000); // 5 segundos
  }

  /**
   * Carregar QR Code
   * O endpoint também cria/inicia a instância caso ela não exista
   * GET /api/v1/WhatsAppInstancias/instances/{sessionId}/qr
   */
  async loadQRCode(): Promise<void> {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) return;
    }

    try {
      const qrResponse = await firstValueFrom(
        this.whatsappInstanciaService.obterQrCode(this.sessionId).pipe(takeUntil(this.destroy$))
      );

      console.log('Resposta do QR Code: =>>>>>', qrResponse);
      
      if (qrResponse?.qr) {
        const qrString = qrResponse.qr;
        
        // Verificar se já está no formato data:image
        if (qrString.startsWith('data:image')) {
          // Já está formatado como data URI
          this.qrCode = qrString;
        } else {
          // Converter string do QR code em imagem Base64
          try {
            // A string recebida é o payload do QR code
            // Pode ter vírgulas (formato do WhatsApp), usar a string completa
            const cleanedQrString = qrString.trim();
            
            console.log('Gerando QR Code a partir da string:', cleanedQrString.substring(0, 50) + '...');
            
            // Converter para imagem QR Code usando a string completa
            this.qrCode = await QRCode.toDataURL(cleanedQrString, {
              errorCorrectionLevel: 'M',
              type: 'image/png',
              margin: 2,
              width: 300,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            
            console.log('QR Code gerado com sucesso');
          } catch (qrError: any) {
            console.error('Erro ao gerar QR Code a partir da string:', qrError);
            console.error('String recebida (primeiros 100 chars):', qrString.substring(0, 100));
            
            // Tentar novamente com configurações diferentes
            try {
              this.qrCode = await QRCode.toDataURL(qrString, {
                errorCorrectionLevel: 'L', // Nível mais baixo de correção
                margin: 1,
                width: 250
              });
              console.log('QR Code gerado na segunda tentativa');
            } catch (retryError: any) {
              console.error('Erro ao gerar QR Code na segunda tentativa:', retryError);
              this.toastService.show('Erro ao gerar imagem do QR Code. Tente atualizar.', {
                classname: 'bg-warning text-dark'
              });
            }
          }
        }
        
        this.qrCodeExpiresAt = qrResponse.expiresAt || null;
        
        // Como o endpoint também cria a instância, atualizar o status após obter QR code
        if (this.status === 'disconnected' || !this.status) {
          // Se estava desconectado, atualizar status para refletir que a instância foi criada
          this.status = 'qr_ready';
        }
        
        // Verificar expiração e atualizar antes de expirar
        if (qrResponse.expiresAt) {
          const expiresAt = new Date(qrResponse.expiresAt);
          const now = new Date();
          const timeUntilExpiry = expiresAt.getTime() - now.getTime();
          
          // Atualizar QR Code 10 segundos antes de expirar
          if (timeUntilExpiry > 0) {
            setTimeout(() => {
              if (this.status === 'qr_ready') {
                this.loadQRCode();
              }
            }, Math.max(0, timeUntilExpiry - 10000));
          }
        }
        
        // Verificar status após obter QR code para garantir sincronização
        setTimeout(async () => {
          await this.loadWhatsAppStatus();
        }, 1000);
      }
    } catch (error: any) {
      if (error.status === 404) {
        // QR Code não disponível, verificar status
        console.log('QR Code não disponível. Verificando status...');
        await this.loadWhatsAppStatus();
      } else if (error.status === 401) {
        this.toastService.show('Erro de autenticação ao obter QR Code.', {
          classname: 'bg-warning text-dark'
        });
      } else {
        console.error('Erro ao carregar QR Code:', error);
      }
    }
  }

  /**
   * Desconectar WhatsApp
   * DELETE /api/v1/WhatsAppInstancias/instances/{sessionId}
   */
  async disconnect(): Promise<void> {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) return;
    }

    const confirmar = confirm('Tem certeza que deseja desconectar o WhatsApp?');
    if (!confirmar) return;

    this.isDisconnecting = true;
    try {
      const response = await firstValueFrom(
        this.whatsappInstanciaService.deletarInstancia(this.sessionId).pipe(takeUntil(this.destroy$))
      );
      
      if (response?.success) {
        this.toastService.show('WhatsApp desconectado com sucesso!', { classname: 'bg-success text-light' });
        this.limparEstado();
        // Recarregar status após desconectar
        setTimeout(() => {
          this.loadWhatsAppStatus();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Erro ao desconectar:', error);
      this.toastService.show('Erro ao desconectar WhatsApp.', {
        classname: 'bg-danger text-light'
      });
    } finally {
      this.isDisconnecting = false;
    }
  }

  /**
   * Reconectar/Criar instância WhatsApp
   * O endpoint de QR code agora também cria a instância se ela não existir
   * GET /api/v1/WhatsAppInstancias/instances/{sessionId}/qr
   */
  async reconnect(): Promise<void> {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) {
        this.toastService.show('Erro: não foi possível obter o ID da sessão.', {
          classname: 'bg-danger text-light'
        });
        return;
      }
    }

    this.isConnecting = true;
    try {
      // O endpoint de QR code agora também cria a instância se não existir
      await this.loadQRCode();
      
      this.toastService.show('Reconexão iniciada! Aguarde o QR Code aparecer.', { 
        classname: 'bg-info text-light' 
      });
      
      // Verificar status após obter QR code
      setTimeout(async () => {
        await this.loadWhatsAppStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Erro ao reconectar:', error);
      
      if (error.status === 404) {
        // Se ainda retornar 404, tentar verificar status
        await this.verificarInstanciaExistente();
      } else if (error.status === 401) {
        this.toastService.show('Erro de autenticação ao reconectar.', {
          classname: 'bg-warning text-dark'
        });
      } else {
        this.toastService.show('Erro ao reconectar WhatsApp.', {
          classname: 'bg-danger text-light'
        });
      }
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Atualizar status manualmente
   */
  refreshStatus(): void {
    this.loadWhatsAppStatus();
  }

  /**
   * Iniciar refresh automático do status
   */
  private startStatusRefresh(): void {
    if (!this.sessionId) {
      this.sessionId = this.getTenantId();
      if (!this.sessionId) return;
    }

    interval(this.statusRefreshInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.whatsappInstanciaService.obterStatus(this.sessionId!)),
        tap(statusResponse => {
          // Não atualizar isLoading aqui, pois é automático
          this.atualizarStatus(statusResponse);
  
          // Se status for "qr_ready" e não estiver aguardando, buscar QR code
          if (statusResponse.status === 'qr_ready' && !this.qrCode && !this.isWaitingForQRCode) {
            this.loadQRCode();
          }
        }),
        takeWhile(statusResponse => statusResponse.status !== 'connected', true) // Para quando conectar
      )
      .subscribe({
        error: (error) => {
          // Ignorar erros 404 (instância não existe)
          if (error.status !== 404) {
            console.error('Erro no refresh automático:', error);
          }
        }
      });
  }

  /**
   * Limpar estado ao desconectar
   */
  private limparEstado(): void {
    this.qrCode = null;
    this.qrCodeExpiresAt = null;
    this.phoneNumber = null;
    this.connectedAt = null;
    this.status = 'disconnected';
    this.isWaitingForQRCode = false;
  }

  /**
   * Obter classe CSS para o status
   */
  getStatusClass(): string {
    if (this.status === 'connected') return 'text-success';
    if (this.status === 'disconnected') return 'text-danger';
    return 'text-warning';
  }

  /**
   * Obter texto do status
   */
  getStatusText(): string {
    switch (this.status) {
      case 'connected':
        return `Conectado${this.phoneNumber ? ` - ${this.phoneNumber}` : ''}`;
      case 'WORKING':
        return 'Online';
      case 'qr_ready':
        return 'Aguardando escaneamento do QR Code';
      case 'initializing':
        return 'Inicializando...';
      case 'disconnected':
        return 'Desconectado';
      default:
        // Para outros status, mostrar o próprio status ou "Em Processo"
        return this.status || 'Desconectado';
    }
  }

  /**
   * Verificar se está conectado (para compatibilidade com template)
   */
  get connected(): boolean {
    return this.status === 'connected';
  }

  /**
   * Carregar configurações do WhatsApp
   */
  async carregarConfiguracoes(): Promise<void> {
    this.isLoadingConfig = true;
    try {
      const response = await firstValueFrom(
        this.configuracoesWhatsAppService.obterConfiguracoes().pipe(takeUntil(this.destroy$))
      );
      
      if (response) {
        this.quantidadeChamadosSimultaneos = response.quantidadeChamadoPorAtendente || 1;
      }
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error);
      
      if (error.status === 404) {
        // Configuração não existe ainda, manter valor padrão
        this.quantidadeChamadosSimultaneos = 1;
      } else if (error.status === 401) {
        this.toastService.show('Erro de autenticação ao carregar configurações.', {
          classname: 'bg-warning text-dark'
        });
      } else {
        this.toastService.show('Erro ao carregar configurações do WhatsApp.', {
          classname: 'bg-danger text-light'
        });
      }
    } finally {
      this.isLoadingConfig = false;
    }
  }

  /**
   * Salvar configurações do WhatsApp
   */
  async salvarConfiguracoes(): Promise<void> {
    // Validar valor
    if (!this.quantidadeChamadosSimultaneos || this.quantidadeChamadosSimultaneos < 1) {
      this.toastService.show('A quantidade de chamados deve ser maior que zero.', {
        classname: 'bg-warning text-dark'
      });
      return;
    }

    this.isSavingConfig = true;
    try {
      const response = await firstValueFrom(
        this.configuracoesWhatsAppService
          .atualizarChamadoPorAtendente(this.quantidadeChamadosSimultaneos)
          .pipe(takeUntil(this.destroy$))
      );
      
      if (response) {
        this.quantidadeChamadosSimultaneos = response.quantidadeChamadoPorAtendente;
        this.toastService.show('Configurações salvas com sucesso!', {
          classname: 'bg-success text-light'
        });
      }
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      
      if (error.status === 401) {
        this.toastService.show('Erro de autenticação ao salvar configurações.', {
          classname: 'bg-warning text-dark'
        });
      } else {
        this.toastService.show('Erro ao salvar configurações. Tente novamente.', {
          classname: 'bg-danger text-light'
        });
      }
    } finally {
      this.isSavingConfig = false;
    }
  }

  /**
   * Obter tenantId do token JWT
   */
  private getTenantId(): string {
    try {
      const currentUser = this.authService.currentUserValue;
      if (currentUser && currentUser.token) {
        const decodedToken: any = jwtDecode(currentUser.token);
        return decodedToken.tenantId || decodedToken.TenantId || '';
      }
      
      // Fallback: tentar obter do localStorage
      const token = localStorage.getItem('accessToken') || localStorage.getItem('jwt_token');
      if (token) {
        const decodedToken: any = jwtDecode(token);
        return decodedToken.tenantId || decodedToken.TenantId || '';
      }
    } catch (error) {
      console.error('Erro ao decodificar token JWT:', error);
    }
    
    return '';
  }
}
